import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, User, Mail, Phone, Building2, Briefcase, ClipboardList, Truck, MapPin, ExternalLink, Pencil, Trash2, Plus, Home } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AsideSection } from "@/components/AsideSection";
import AddressLookup from "@/components/AddressLookup";
import S3Uploader from "@/components/S3Uploader";
import { CustomerWhatsAppBadge } from "@/components/whatsapp/CustomerWhatsAppBadge";
import { CustomerInstallationMedia } from "@/components/CustomerInstallationMedia";
import { CustomerTrustpilotReviews } from "@/components/TrustpilotReviews";
import { toast } from "sonner";
import { CustomerAnalytics } from "@/components/analytics/CustomerAnalytics";
import CustomerMarketingTab from "@/components/CustomerMarketingTab";
import CustomerAutomationsTab from "@/components/email/CustomerAutomationsTab";
import { MarketingSubscriptions, MarketingToggles } from "@/components/MarketingSubscriptions";
import { MarketingActivityCard } from "@/components/email/MarketingActivityCard";
import JobSmsPanel from "@/components/JobSmsPanel";
import { Megaphone } from "lucide-react";

function Val({ children }) {
  if (children === "••••••••") return <span className="text-slate-400 tracking-widest">••••••••</span>;
  return children ? <span className="text-slate-800">{children}</span> : <span className="text-slate-300">—</span>;
}
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { masking, can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState("profile");
  const [delOpen, setDelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ addresses: [] });

  useEffect(() => {
    setData(null); setError(false);
    api.get(`/contacts/${id}`, { params: { mask: masking } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id, masking]);

  if (error) return <div className="p-10 text-center" data-testid="user-not-found"><p className="text-slate-500">Customer {id} not found.</p><Button variant="outline" className="mt-4" onClick={() => navigate("/users")}>Back to Customers</Button></div>;
  if (!data) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading user…</div>;

  const c = data.contact;
  const canEdit = data.can_edit ?? can("customers.edit");
  const assocToAddr = (a) => ({
    label: a.venue_name || "Site", line1: a.address || "", line2: "",
    city: "", postcode: a.postcode || "", country: "United Kingdom",
  });
  const openEdit = () => {
    setForm({
      full_name: c.full_name || "", email: c.email || "", telephone: c.telephone || "",
      mobile: c.mobile || "", company: c.company || "", job_title: c.job_title || "",
      notes: c.notes || "", user_type: c.user_type || "",
      whatsapp_subscribed: !!c.marketing?.whatsapp_subscribed, newsletter_subscribed: !!c.marketing?.newsletter_subscribed,
      addresses: (c.addresses && c.addresses.length)
        ? c.addresses.map((a) => ({ ...a }))
        : (data.associated_addresses || []).map(assocToAddr),
    });
    setEditOpen(true);
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (i, k, v) => setForm((f) => { const a = [...f.addresses]; a[i] = { ...a[i], [k]: v }; return { ...f, addresses: a }; });
  const addAddr = () => setForm((f) => ({ ...f, addresses: [...f.addresses, { label: "", line1: "", line2: "", city: "", postcode: "", country: "" }] }));
  const onLookup = (a) => setForm((f) => ({ ...f, addresses: [...f.addresses, { label: "", line1: a.address_line_1 || "", line2: a.address_line_2 || a.county || "", city: a.town_city || "", postcode: a.postcode || "", country: a.country || "United Kingdom" }] }));
  const rmAddr = (i) => setForm((f) => ({ ...f, addresses: f.addresses.filter((_, x) => x !== i) }));
  const importAssoc = () => setForm((f) => {
    const key = (a) => `${(a.line1 || "").trim().toLowerCase()}|${(a.postcode || "").trim().toLowerCase()}`;
    const have = new Set(f.addresses.map(key));
    const extra = (data.associated_addresses || []).map(assocToAddr).filter((a) => !have.has(key(a)));
    if (!extra.length) { toast.info("No new addresses to import from linked jobs"); return f; }
    toast.success(`Imported ${extra.length} address${extra.length > 1 ? "es" : ""} from linked jobs`);
    return { ...f, addresses: [...f.addresses, ...extra] };
  });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/contacts/${id}`, form);
      toast.success("Customer updated");
      setEditOpen(false);
      const fresh = await api.get(`/contacts/${id}`, { params: { mask: masking } });
      setData(fresh.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/contacts/${id}`); toast.success("Customer deleted"); navigate("/users"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); setDeleting(false); }
  };
  return (
    <div className="p-6 lg:p-8" data-testid="user-detail">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg">
          {(c.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight break-words" data-testid="user-detail-name">{c.full_name || "—"}</h1>
          {c.legacy_id && <CustomerWhatsAppBadge customerId={c.legacy_id} />}
          <div className="flex items-center gap-2 mt-0.5">
            {c.user_type && <Pill colour={c.user_type === "Employee" ? "blue" : c.user_type === "Client Contact" ? "purple" : "slate"}>{c.user_type}</Pill>}
            <span className="text-xs text-slate-400 mono">ID {c.legacy_id}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={() => window.open("/portal/demo", "_blank", "noopener")} data-testid="view-as-customer-btn">
            <ExternalLink className="w-4 h-4" /> View as Customer
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" className="gap-1.5" onClick={openEdit} data-testid="user-edit-btn"><Pencil className="w-4 h-4" /> Edit</Button>
              <Button variant="outline" className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDelOpen(true)} data-testid="user-delete-btn"><Trash2 className="w-4 h-4" /> Delete</Button>
            </>
          )}
        </div>
      </div>

      {c.user_type !== "Employee" && (
        <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" role="tablist">
          {[["profile", "Profile"], ["analytics", "Overview & Analytics"], ["marketing", "Marketing"], ["automations", "Automations"]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`user-tab-${k}`}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap shrink-0 transition-colors ${tab === k ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "automations" && c.user_type !== "Employee" ? <CustomerAutomationsTab customerId={c.legacy_id} /> : tab === "marketing" && c.user_type !== "Employee" ? <CustomerMarketingTab customerId={c.legacy_id} /> : tab === "analytics" && c.user_type !== "Employee" ? <CustomerAnalytics customerId={c.legacy_id} /> : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><User className="w-4 h-4 text-indigo-500" /> Contact Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              <Field label="Email"><Val>{c.email}</Val></Field>
              <Field label="Telephone"><Val>{c.telephone}</Val></Field>
              <Field label="Mobile"><Val>{c.mobile}</Val></Field>
              <Field label="Company"><Val>{c.company}</Val></Field>
              <Field label="Job Title"><Val>{c.job_title}</Val></Field>
              <Field label="Notes"><Val>{c.notes}</Val></Field>
            </div>
            <MarketingSubscriptions m={c.marketing} />
            <p className="text-[11px] text-slate-400 mt-4">Date of birth, home address and payroll information are never displayed in this demonstration.</p>
          </section>

          {(c.addresses?.length > 0) && (
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="user-saved-addresses">
              <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><Home className="w-4 h-4 text-emerald-500" /> Addresses ({c.addresses.length})</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {c.addresses.map((a, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`user-saved-address-${i}`}>
                    {a.label && <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{a.label}</div>}
                    <div className="text-sm text-slate-800">{[a.line1, a.line2, a.city, a.postcode, a.country].filter(Boolean).join(", ") || "—"}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.employee && (
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="user-employee-info">
              <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><Briefcase className="w-4 h-4 text-blue-500" /> Employee Role Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                <Field label="Employee Number"><Val>{data.employee.employee_number}</Val></Field>
                <Field label="Roles"><Val>{data.employee.roles}</Val></Field>
                <Field label="Departments"><Val>{data.employee.departments}</Val></Field>
                <Field label="Depots"><Val>{data.employee.depots}</Val></Field>
              </div>
            </section>
          )}

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="user-associated-addresses">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-rose-500" /> Associated Addresses ({data.associated_addresses?.length || 0})</h2>
            {(data.associated_addresses?.length || 0) === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No associated addresses found from linked jobs.</div>
            ) : (
              <div className="space-y-2">
                {data.associated_addresses.map((a, i) => (
                  <Link key={i} to={`/tasks/${a.task_id}`} className="flex items-start gap-2.5 py-2 px-2 -mx-2 rounded-md hover:bg-slate-50" data-testid={`user-address-${i}`}>
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{a.address}</div>
                      {a.postcode && <div className="text-xs text-slate-400 mono">{a.postcode} · from task {a.task_id}</div>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">Derived from venues on this user's linked jobs — home addresses are never shown.</p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="user-linked-tasks">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-indigo-500" /> Linked Jobs ({data.linked_tasks.length})</h2>
            {data.linked_tasks.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No linked jobs found.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.linked_tasks.map((t) => (
                  <Link key={t.legacy_id} to={`/tasks/${t.legacy_id}`} className="flex items-center justify-between py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-md" data-testid={`user-task-${t.legacy_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{t.legacy_id}: {t.title}</div>
                      <div className="text-xs text-slate-400">{t.created_at}</div>
                    </div>
                    {t.status && <Pill colour={t.status_colour}>{t.status}</Pill>}
                  </Link>
                ))}
              </div>
            )}
          </section>

          {c.legacy_id && c.user_type !== "Employee" && <JobSmsPanel customerId={c.legacy_id} customerName={c.full_name} jobs={data.linked_tasks || []} defaultOpen />}
        </div>

        <div className="space-y-6">
          {c.legacy_id && <CustomerInstallationMedia customerId={c.legacy_id} />}
          {c.legacy_id && <CustomerTrustpilotReviews customerId={c.legacy_id} />}
          {c.legacy_id && c.user_type !== "Employee" && <MarketingActivityCard customerId={c.legacy_id} />}
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="user-documents">
            <h2 className="text-base font-bold text-slate-900 mb-3">Documents</h2>
            <S3Uploader recordType="customers" recordId={id} category="Customer Document" canManage={canEdit} serverSide />
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-purple-500" /> Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Customer Type</span><span className="text-slate-800 font-medium">{c.user_type || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Company</span><span className="text-slate-800 font-medium text-right">{c.company || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Linked Jobs</span><span className="text-slate-800 font-medium">{data.linked_tasks.length}</span></div>
            </div>
          </section>
          {c.user_type === "Employee" && (
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="user-vehicle">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><Truck className="w-4 h-4 text-blue-500" /> Vehicle Assignment</h2>
            <div className="text-center text-sm text-slate-400 py-4 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No vehicle assignment available.</div>
          </section>
          )}
        </div>
      </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle>Edit customer</DialogTitle></DialogHeader>
          <AsideSection first icon={User} title="Contact details" description="Name, contact channels and notes for this user.">
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Full name</span>
              <Input value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="mt-1" data-testid="user-edit-name" /></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</span>
                <Input value={form.email} readOnly={form.email === "••••••••"} onChange={(e) => setF("email", e.target.value)} className="mt-1" data-testid="user-edit-email" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type</span>
                <Input value={form.user_type} onChange={(e) => setF("user_type", e.target.value)} className="mt-1" data-testid="user-edit-type" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Telephone</span>
                <Input value={form.telephone} readOnly={form.telephone === "••••••••"} onChange={(e) => setF("telephone", e.target.value)} className="mt-1" data-testid="user-edit-telephone" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile</span>
                <Input value={form.mobile} readOnly={form.mobile === "••••••••"} onChange={(e) => setF("mobile", e.target.value)} className="mt-1" data-testid="user-edit-mobile" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</span>
                <Input value={form.company} onChange={(e) => setF("company", e.target.value)} className="mt-1" data-testid="user-edit-company" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job title</span>
                <Input value={form.job_title} onChange={(e) => setF("job_title", e.target.value)} className="mt-1" data-testid="user-edit-jobtitle" /></label>
            </div>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</span>
              <textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} rows={2}
                className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="user-edit-notes" /></label>
            {masking && <p className="text-[11px] text-amber-600">Masked fields (••••) are locked — turn off “Mask data” to edit contact details.</p>}
          </AsideSection>
          <AsideSection icon={Megaphone} title="Marketing subscriptions" description="Whether this customer agreed to WhatsApp marketing and the email newsletter.">
            <MarketingToggles prefix="user-edit" wa={form.whatsapp_subscribed} newsletter={form.newsletter_subscribed} blocked={c.marketing?.whatsapp_blocked} onChange={setF} />
          </AsideSection>
          <AsideSection icon={MapPin} title="Addresses" description="The user's actual postal addresses. Look up by postcode, pull in the addresses used on their linked jobs, then edit or remove as needed.">
            <div className="space-y-3">
              <AddressLookup onSelect={onLookup} />
              {form.addresses.map((a, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`user-edit-address-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Label (e.g. Home, Site)" value={a.label || ""} onChange={(e) => setAddr(i, "label", e.target.value)} className="h-8" data-testid={`addr-label-${i}`} />
                    <button onClick={() => rmAddr(i)} className="text-rose-500 hover:bg-rose-50 rounded p-1.5 shrink-0" data-testid={`addr-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <Input placeholder="Address line 1" value={a.line1 || ""} onChange={(e) => setAddr(i, "line1", e.target.value)} className="h-8" data-testid={`addr-line1-${i}`} />
                  <Input placeholder="Address line 2" value={a.line2 || ""} onChange={(e) => setAddr(i, "line2", e.target.value)} className="h-8" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" value={a.city || ""} onChange={(e) => setAddr(i, "city", e.target.value)} className="h-8" />
                    <Input placeholder="Postcode" value={a.postcode || ""} onChange={(e) => setAddr(i, "postcode", e.target.value)} className="h-8" data-testid={`addr-postcode-${i}`} />
                    <Input placeholder="Country" value={a.country || ""} onChange={(e) => setAddr(i, "country", e.target.value)} className="h-8" />
                  </div>
                </div>
              ))}
              {(data.associated_addresses?.length || 0) > 0 && (
                <Button variant="outline" className="gap-1.5 w-full border-dashed border-rose-200 text-rose-600 hover:bg-rose-50" onClick={importAssoc} data-testid="addr-import-assoc"><MapPin className="w-4 h-4" /> Import {data.associated_addresses.length} from linked jobs</Button>
              )}
              <Button variant="outline" className="gap-1.5 w-full border-dashed" onClick={addAddr} data-testid="addr-add"><Plus className="w-4 h-4" /> Add address</Button>
            </div>
          </AsideSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={saving} onClick={save} data-testid="user-edit-save">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="user-delete-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>Delete this user?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This permanently removes <b>{c.full_name || id}</b> and their saved details. Linked jobs are not affected. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={deleting} onClick={doDelete} data-testid="user-delete-confirm">{deleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Delete user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
